import FJAIVideoGeneratorLeft from '../view/FJAIVideoGeneratorLeft';
import useLocalStore from '../../../../hooks/useLocalStore.js';
import { useSelector, useDispatch } from 'react-redux';
import { useCallback, useEffect, useMemo } from 'react';
import FJActionAIVideoGenerator from '../../../../redux/action/FJActionAIVideoGenerator.js';
import FJActionAiHistoryCommon from '../../../../redux/action/FJActionAiHistoryCommon.js';
import FJActionModal from '../../../../redux/action/FJActionModal.js';
import FJUtil from '../../../../util/FJUtil.js';
import {
    AI_GENERATOR_VIDEO_TAB_TYPE,
    AI_VIDEO_GENERATION_MODEL_LIST,
} from '../../../../config/dataConfig/FJAIVideoGeneratorData.js';
import useDefaultFreeLimit from '../../../../hooks/useDefaultFreeLimit.jsx';
import { setSliceMotionControlTextSwitch } from '../../../../redux/slices/FJSliceAIVideoGenerator.js';

const FJAIVideoGeneratorLeftContainer = () => {
    useLocalStore();
    const dispatch = useDispatch();
    const currentRatio = useSelector(state => state.AIVideoGenerator.currentRatio);
    const currentQuality = useSelector(state => state.AIVideoGenerator.currentQuality);
    const currentDuration = useSelector(state => state.AIVideoGenerator.currentDuration);
    const currentResolution = useSelector(state => state.AIVideoGenerator.currentResolution);
    const tabIndex = useSelector(state => state.AIVideoGenerator.tabIndex);
    const tvTextValue = useSelector(state => state.AIVideoGenerator.tvTextValue);
    const ivTextValue = useSelector(state => state.AIVideoGenerator.ivTextValue);
    const evTextValue = useSelector(state => state.AIVideoGenerator.evTextValue);

    const audioActive = useSelector(state => state.AIVideoGenerator.audioActive);

    const taskStatus = useSelector(state => state.common.taskStatus);
    const isProjectLoading = useSelector(state => state.common.isProjectLoading);
    const creditNumber = useSelector(state => state.common.creditNumber);

    const ivFreeNum = useSelector(state => state.AIVideoGenerator.ivFreeNum);
    const tvFreeNum = useSelector(state => state.AIVideoGenerator.tvFreeNum);
    const evFreeNum = useSelector(state => state.AIVideoGenerator.evFreeNum);
    const imageMode = useSelector(state => state.AIVideoGenerator.imageMode);

    const currentModel = useSelector(state => state.AIVideoGenerator.currentModel);
    const curCharacterOrientation = useSelector(state => state.AIVideoGenerator.curCharacterOrientation);
    const motionControlTextSwitch = useSelector(state => state.AIVideoGenerator.motionControlTextSwitch);

    const totalCredit = useSelector(state => {
        const currentModelConfig = AI_VIDEO_GENERATION_MODEL_LIST.find(model => model.value === currentModel);
        const creditPrice =
            (audioActive && currentModelConfig?.creditPriceWidThAudio) ||
            currentModelConfig?.creditPriceLevels?.[currentResolution] ||
            currentModelConfig?.creditPrice ||
            2;
        const totalCredit = creditPrice * currentDuration;
        const aiSfxTotalCredit = currentModelConfig?.creditPriceWidThAudio
            ? 0
            : state.AIVideoGenerator.aiSfxTotalCredit;
        return totalCredit + aiSfxTotalCredit;
    });

    const creditPrice = useSelector(state => state.AIVideoGenerator.creditPrice);

    const freeNumCreation = useMemo(() => {
        if (tabIndex === AI_GENERATOR_VIDEO_TAB_TYPE.AI_IMAGE_TO_VIDEO) {
            return ivFreeNum;
        } else if (tabIndex === AI_GENERATOR_VIDEO_TAB_TYPE.AI_TEXT_TO_VIDEO) {
            return tvFreeNum;
        } else {
            return evFreeNum;
        }
    }, [tabIndex, ivFreeNum, tvFreeNum, evFreeNum]);

    const currentFreeLimitType = useMemo(() => {
        if (tabIndex === AI_GENERATOR_VIDEO_TAB_TYPE.AI_IMAGE_TO_VIDEO) {
            return 'image-to-video';
        } else if (tabIndex === AI_GENERATOR_VIDEO_TAB_TYPE.AI_TEXT_TO_VIDEO) {
            return 'text-to-video';
        } else {
            return null;
        }
    }, [tabIndex]);

    const defaultFreeNumCreation = useDefaultFreeLimit(currentFreeLimitType);

    const textValue = useMemo(() => {
        if (tabIndex === AI_GENERATOR_VIDEO_TAB_TYPE.AI_IMAGE_TO_VIDEO) {
            return ivTextValue;
        } else if (tabIndex === AI_GENERATOR_VIDEO_TAB_TYPE.AI_TEXT_TO_VIDEO) {
            return tvTextValue;
        } else if (tabIndex === AI_GENERATOR_VIDEO_TAB_TYPE.AI_EFFECTS_VIDEO) {
            return evTextValue;
        }
    }, [ivTextValue, tvTextValue, tabIndex, evTextValue]);

    useEffect(() => {
        window.addEventListener('storage', function (event) {
            if (event.key === 'communicationKey') {
                try {
                    dispatch(FJActionAiHistoryCommon.initAiTextToImageHistoryList(true));
                    dispatch(FJActionAiHistoryCommon.initAiImageToImageHistoryList(true));
                } catch (e) {
                    console.log(e);
                }
            }
        });
    }, []);

    const changeRatio = useCallback(
        ratio => {
            dispatch(FJActionAIVideoGenerator.changeRatioAction(ratio));
        },
        [dispatch],
    );

    const onChangeQuality = useCallback(
        quality => {
            if (quality === 2 && FJUtil.isLogin()) {
                if (totalCredit > creditNumber) {
                    dispatch(FJActionModal.showUpgradeModalAction());
                    dispatch(FJActionAIVideoGenerator.changeQualityAction(1));
                    return;
                }
            }
            dispatch(FJActionAIVideoGenerator.changeQualityAction(quality));
        },
        [dispatch, creditNumber, totalCredit],
    );

    const onChangeDuration = useCallback(
        duration => {
            if (duration !== currentDuration) {
                if (duration * creditPrice > creditNumber && FJUtil.isLogin()) {
                    dispatch(FJActionModal.showUpgradeModalAction());
                    return;
                }
            }
            dispatch(FJActionAIVideoGenerator.changeDurationAction(duration));
        },
        [currentDuration, dispatch, creditNumber, creditPrice],
    );

    const onTextValueChange = useCallback(
        value => {
            dispatch(FJActionAIVideoGenerator.changeTextValueAction(value));
        },
        [dispatch],
    );

    const onGenerate = useCallback(() => {
        dispatch(FJActionAIVideoGenerator.onGenerateAction());
    }, [dispatch]);

    const getInspire = useCallback(() => {
        dispatch(FJActionAIVideoGenerator.getInspireAction());
    }, [dispatch]);

    const onChangeAudioActive = useCallback(
        value => {
            dispatch(FJActionAIVideoGenerator.toggleAudioActiveAction(value));
        },
        [dispatch],
    );

    const onChangeModel = useCallback(
        model => {
            dispatch(FJActionAIVideoGenerator.changeModelAction(model));
        },
        [dispatch],
    );

    const onChangeResolution = useCallback(
        resolution => {
            dispatch(FJActionAIVideoGenerator.changeResolutionAction(resolution));
        },
        [dispatch],
    );
    const onChangeCharacterOrientation = useCallback(
        orientation => {
            dispatch(FJActionAIVideoGenerator.changeCharacterOrientationAction(orientation));
        },
        [dispatch],
    );
    const onMotionControlPresetClick = useCallback(
        item => {
            dispatch(FJActionAIVideoGenerator.onMotionControlPresetClickAction(item));
        },
        [dispatch],
    );
    const setMotionControlTextSwitch = useCallback(
        value => {
            dispatch(setSliceMotionControlTextSwitch(value));
        },
        [dispatch],
    );

    return (
        <FJAIVideoGeneratorLeft
            tabIndex={tabIndex}
            currentRatio={currentRatio}
            changeRatio={changeRatio}
            currentQuality={currentQuality}
            currentDuration={currentDuration}
            totalCredit={totalCredit}
            onChangeQuality={onChangeQuality}
            onChangeDuration={onChangeDuration}
            textValue={textValue}
            onTextValueChange={onTextValueChange}
            onGenerate={onGenerate}
            getInspire={getInspire}
            taskStatus={taskStatus}
            isProjectLoading={isProjectLoading}
            freeNumCreation={freeNumCreation}
            creditNumber={creditNumber}
            audioActive={audioActive}
            onChangeAudioActive={onChangeAudioActive}
            currentModel={currentModel}
            defaultFreeNumCreation={defaultFreeNumCreation}
            onChangeModel={onChangeModel}
            currentResolution={currentResolution}
            onChangeResolution={onChangeResolution}
            imageMode={imageMode}
            onChangeCharacterOrientation={onChangeCharacterOrientation}
            curCharacterOrientation={curCharacterOrientation}
            onMotionControlPresetClick={onMotionControlPresetClick}
            motionControlTextSwitch={motionControlTextSwitch}
            setMotionControlTextSwitch={setMotionControlTextSwitch}
        />
    );
};
export default FJAIVideoGeneratorLeftContainer;
